import { useEffect, useState } from "react";
import { getDashboardStats, listTransactions } from "@/lib/firestore";
import { useAuth } from "@/context/AuthContext";
import { DASH } from "@/constants/testIds";
import { Link } from "react-router-dom";
import {
  Wrench,
  CheckCircle,
  Package,
  UserGear,
  Warning,
  ClipboardText,
  ArrowsClockwise,
  Timer,
} from "@phosphor-icons/react";

function KPI({ label, value, sub, testId, color = "#FF4F00", icon: Icon }) {
  return (
    <div
      data-testid={testId}
      className="brutal-card p-6 relative"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">{label}</div>
        {Icon && <Icon size={20} weight="bold" style={{ color }} />}
      </div>
      <div className="font-mono font-bold text-4xl leading-none" style={{ color }}>
        {value}
      </div>
      {sub && <div className="mt-3 text-xs text-[#A1A1AA] font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          getDashboardStats(user),
          listTransactions(user),
        ]);
        setStats(s);
        setRecent(t.slice(0, 6));
      } catch (e) {
        // noop
      }
    })();
  }, [user]);

  const isAdmin = user?.role === "admin";
  const canReport = user?.permissions?.can_report_damage !== false;
  const canRequest = user?.permissions?.can_request_tools !== false;

  return (
    <div className="space-y-8">
      <div>
        <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">
          // WELCOME, {user?.name?.toUpperCase()}
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl leading-none">
          {isAdmin ? "SHOP FLOOR OVERVIEW" : "MY WORKSTATION"}
        </h1>
        <div className="mt-2 text-[#A1A1AA] text-sm">
          {isAdmin
            ? "Live inventory, pending approvals, active issues."
            : "Your active tools, pending requests, and reports."}
        </div>
      </div>

      {/* KPI Grid — Primary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          testId={DASH.kpiTotalTools}
          label="Total Tools"
          value={stats?.total_tools ?? "—"}
          sub={`${stats?.total_qty ?? 0} total units`}
          icon={Wrench}
          color="#F5F5F5"
        />
        <KPI
          testId={DASH.kpiAvailable}
          label="Available"
          value={stats?.available_qty ?? "—"}
          sub="ready to issue"
          icon={CheckCircle}
          color="#00E676"
        />
        <KPI
          testId={DASH.kpiIssued}
          label={isAdmin ? "Currently Issued" : "My Active Tools"}
          value={isAdmin ? (stats?.issued_qty ?? "—") : (stats?.active_transactions ?? "—")}
          sub={isAdmin ? `${stats?.active_transactions ?? 0} active txns` : "checked out to you"}
          icon={ArrowsClockwise}
          color="#FF4F00"
        />
        <KPI
          testId={DASH.kpiPendingUsers}
          label={isAdmin ? "Pending Users" : "My Requests"}
          value={isAdmin ? (stats?.pending_users ?? "—") : (stats?.pending_requests ?? "—")}
          sub={isAdmin ? "awaiting approval" : "pending review"}
          icon={UserGear}
          color="#FFD700"
        />
      </div>

      {/* KPI Grid — Secondary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          testId={DASH.kpiPendingDamage}
          label={isAdmin ? "Damage Reports (Pending)" : "My Damage Reports"}
          value={stats?.pending_damage ?? "—"}
          icon={Warning}
          color="#D32F2F"
        />
        <KPI
          testId={DASH.kpiPendingRequests}
          label={isAdmin ? "Overdue Tools" : "Tool Requests"}
          value={isAdmin ? (stats?.overdue ?? "—") : (stats?.pending_requests ?? "—")}
          sub={isAdmin ? "past due date" : "your requests"}
          icon={isAdmin ? Timer : ClipboardText}
          color={isAdmin ? "#D32F2F" : "#FFD700"}
        />
        <KPI
          testId={DASH.kpiMaintenanceOverdue}
          label="Maintenance Overdue"
          value={stats?.maintenance_overdue ?? "—"}
          sub={`${stats?.total_schedules ?? 0} active schedules`}
          icon={Warning}
          color="#D32F2F"
        />
        <KPI
          testId={DASH.kpiMaintenanceDueSoon}
          label="Maintenance Due Soon"
          value={stats?.maintenance_due_soon ?? "—"}
          sub="≤3 days or ≤1 use"
          icon={Timer}
          color="#FFD700"
        />
        {isAdmin && (
          <KPI
            testId={DASH.kpiPendingApprovals}
            label="Pending Approvals"
            value={stats?.pending_requests ?? "—"}
            sub="tool, category, location requests"
            icon={ClipboardText}
            color="#FFD700"
          />
        )}
      </div>

      {/* Quick actions + recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="brutal-card p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">
              // Recent Transactions
            </div>
            <Link to="/transactions" className="text-xs text-[#FF4F00] hover:underline font-mono uppercase tracking-widest">
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-[#52525B] text-sm py-8 text-center font-mono">// no transactions yet //</div>
          ) : (
            <div className="space-y-2">
              {recent.map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-[#1a1a1e] py-2 last:border-0">
                  <div>
                    <div className="text-sm font-semibold">{t.tool_name}</div>
                    <div className="text-xs text-[#52525B] font-mono">
                      #{t.tool_code} · to {t.user_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                        t.status === "issued"
                          ? "border-[#FF4F00] text-[#FF4F00]"
                          : "border-[#00E676] text-[#00E676]"
                      }`}
                    >
                      {t.status}
                    </div>
                    <div className="text-[10px] text-[#52525B] font-mono mt-1">
                      {new Date(t.issue_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="brutal-card p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-4">
            // Quick Actions
          </div>
          <div className="space-y-2">
            <Link to="/tools" className="block brutal-btn w-full text-center bg-[#FF4F00] text-black py-2.5 hover:bg-[#FF6B2B] transition-colors duration-150">
              Browse Tools
            </Link>
            <Link to="/transactions" className="block brutal-btn w-full text-center border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] py-2.5 transition-colors duration-150">
              {isAdmin ? "Issue / Receive" : "My Transactions"}
            </Link>
            <Link to="/maintenance" className="block brutal-btn w-full text-center border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] py-2.5 transition-colors duration-150">
              Maintenance
            </Link>
            {canReport && (
              <Link to="/damage-reports" className="block brutal-btn w-full text-center border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] py-2.5 transition-colors duration-150">
                Report Damage
              </Link>
            )}
            {canRequest && (
              <Link to="/tool-requests" className="block brutal-btn w-full text-center border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] py-2.5 transition-colors duration-150">
                Request New Tool
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
